/**
 * Invite tracking on the device (plan.md step 19, the user, 2026-10-15) — the
 * client half of `backend/src/modules/attendance/inviteTracking.ts`.
 *
 * On a rave host whose address carries `?invite=<code>` (`inviteCodeIn` reads
 * it even with a share sheet's text glued on):
 *
 * 1. **The open** — `POST /public/invites/:code/open` with this device's
 *    random id (`myrave:platform:invite-client`, made once, kept). Never
 *    awaited by anything on screen; sent once per code per device (the codes
 *    already reported are `myrave:platform:invite-opened`), and again on the
 *    next load only if it never reached the server. Sent after the session is
 *    known, so the inviter opening their own link is recognised.
 * 2. **The pending code** — `myrave:platform:pending-invite` `{ code, at }`,
 *    kept through sign-in (the login host is another origin; this one is where
 *    the person comes back to). Once signed in inside the rave,
 *    `claimPendingInvite` hands it over (`POST /editions/:id/invite/claim`)
 *    and forgets it on any definitive answer — stored, already attending,
 *    already claimed, refused — and keeps it when the server could not be
 *    reached, or could not decide (401/403/429/5xx).
 *
 * A browser-framed preview (`isFramed`, `inPreview`) never reports anything.
 *
 * **Step 89 (2026-10-29):** the link also says how it was shared (`?via=qr|share|wa|copy`); it is
 * kept with the code and sent with the open and the claim. A code with no rave — an invite to
 * MyRave itself — is claimed wherever the person signs in (`claimPendingMyRaveInvite`, through
 * `POST /public/invites/:code/claim`); a rave's code answers `rave` there and stays pending for
 * that rave. Both claims carry this device's id: a guest's own code is refused.
 */
import { inviteCodeIn, inviteViaIn, INVITE_PARAM, INVITE_VIA_PARAM, type InviteVia } from '@myrave/shared/publicSite';
import { api, ApiError } from './api';
import { isFramed } from './framed';
import { inPreview } from './preview';
import { readJson, remove, writeJson } from './storage';

const CLIENT_KEY = 'invite-client';
const PENDING_KEY = 'pending-invite';
const OPENED_KEY = 'invite-opened';
/** The invite this device arrived with — kept after the claim, for the rave's own words (2026-10-27). */
const ARRIVED_KEY = 'invite-arrived';
/** A pending invite older than this is not why somebody is here any more. */
export const PENDING_MAX_AGE_MS = 30 * 24 * 3_600_000;
const OPENED_MAX = 20;

export interface PendingInvite { code: string; at: number; via?: InviteVia | null }

/** This device's own random id for invite opens — made once, then kept. */
export function inviteClientId(): string {
  const kept = readJson<string | null>(null, CLIENT_KEY, null);
  if (typeof kept === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(kept)) return kept;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const made = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  writeJson(null, CLIENT_KEY, made);
  return made;
}

export function pendingInvite(now = Date.now()): PendingInvite | null {
  const p = readJson<PendingInvite | null>(null, PENDING_KEY, null);
  if (!p || typeof p.code !== 'string' || typeof p.at !== 'number') return null;
  if (now - p.at > PENDING_MAX_AGE_MS) { remove(null, PENDING_KEY); return null; }
  return p;
}

const inPreviewFrame = () => isFramed() || inPreview();

/**
 * The code in this page's address, remembered as pending — synchronously, at
 * boot, before anything can navigate away. Answers the code (or null).
 */
export function noteInviteArrival(search = typeof window === 'undefined' ? '' : window.location.search, now = Date.now()): string | null {
  if (inPreviewFrame()) return null;
  const params = new URLSearchParams(search);
  const code = inviteCodeIn(params.get(INVITE_PARAM));
  if (!code) return null;
  const via = inviteViaIn(params.get(INVITE_VIA_PARAM));
  writeJson(null, PENDING_KEY, { code, at: now, via } satisfies PendingInvite);
  writeJson(null, ARRIVED_KEY, { code, at: now, via } satisfies PendingInvite);
  return code;
}

/**
 * `?invite=<code>&via=<way>` of an address, or `''` — what a hop to another host carries (step 89:
 * the public site sends a signed-in visitor, and the phone app, on to `app.` with it).
 */
export function inviteQuery(search: string): string {
  const params = new URLSearchParams(search);
  const code = inviteCodeIn(params.get(INVITE_PARAM));
  if (!code) return '';
  const out = new URLSearchParams({ [INVITE_PARAM]: code });
  const via = inviteViaIn(params.get(INVITE_VIA_PARAM));
  if (via) out.set(INVITE_VIA_PARAM, via);
  return `?${out.toString()}`;
}

/**
 * The invite this device arrived with, while it is fresh — NOT forgotten by the claim, so the
 * Attend page can still say who invited ("Dana invited you to join OZORA 2026") once the code
 * was handed over, and the sign-in strip whom the person would join.
 */
export function arrivedInvite(now = Date.now()): PendingInvite | null {
  const p = readJson<PendingInvite | null>(null, ARRIVED_KEY, null);
  if (!p || typeof p.code !== 'string' || typeof p.at !== 'number') return null;
  if (now - p.at > PENDING_MAX_AGE_MS) { remove(null, ARRIVED_KEY); return null; }
  return p;
}

let reporting: string | null = null;

/** How the link this device arrived with was shared — for the open and the claim. */
const viaOf = (code: string): InviteVia | null => {
  const a = arrivedInvite();
  return a?.code === code && a.via ? a.via : null;
};

/** Report this device opening `code` — fire and forget; returns void so nothing can wait on it. */
export function reportInviteOpen(code: string): void {
  if (inPreviewFrame()) return;
  const opened = readJson<string[]>(null, OPENED_KEY, []);
  if (reporting === code || (Array.isArray(opened) && opened.includes(code))) return;
  reporting = code;
  const done = () => {
    const list = readJson<string[]>(null, OPENED_KEY, []);
    writeJson(null, OPENED_KEY, [...(Array.isArray(list) ? list : []).filter((c) => c !== code), code].slice(-OPENED_MAX));
  };
  const via = viaOf(code);
  void api.post(`/public/invites/${encodeURIComponent(code)}/open`, { clientId: inviteClientId(), ...(via ? { via } : {}) })
    .then(done)
    .catch((err: unknown) => {
      // An unknown code will not become known: do not send it again.
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) done();
    })
    .finally(() => { if (reporting === code) reporting = null; });
}

/** Statuses after which the pending code has been dealt with, one way or the other. */
const DEFINITIVE = new Set([400, 404, 409]);

let claiming = false;

/**
 * Hand the pending code to the server for this rave (signed in). Resolves
 * `true` when the server answered definitively and the code was forgotten.
 */
export async function claimPendingInvite(editionId: string): Promise<boolean> {
  if (inPreviewFrame() || claiming) return false;
  const pending = pendingInvite();
  if (!pending) return false;
  claiming = true;
  try {
    await api.post(`/editions/${encodeURIComponent(editionId)}/invite/claim`, {
      code: pending.code, clientId: inviteClientId(), ...(pending.via ? { via: pending.via } : {}),
    });
    remove(null, PENDING_KEY);
    return true;
  } catch (err) {
    if (err instanceof ApiError && DEFINITIVE.has(err.status)) {
      remove(null, PENDING_KEY);
      return true;
    }
    // No signal, or a server that could not decide: keep it for the next time.
    return false;
  } finally {
    claiming = false;
  }
}

/**
 * Hand a pending MyRave invite (step 89) to the server, signed in, wherever the person is. A
 * rave's code (`status: 'rave'`) is left pending for that rave's own claim. Resolves `true` when
 * the code was dealt with and forgotten.
 */
export async function claimPendingMyRaveInvite(): Promise<boolean> {
  if (inPreviewFrame() || claiming) return false;
  const pending = pendingInvite();
  if (!pending) return false;
  claiming = true;
  try {
    const answer = await api.post<{ status: string }>(`/public/invites/${encodeURIComponent(pending.code)}/claim`, {
      clientId: inviteClientId(), ...(pending.via ? { via: pending.via } : {}),
    });
    if (answer?.status === 'rave') return false;
    remove(null, PENDING_KEY);
    return true;
  } catch (err) {
    if (err instanceof ApiError && DEFINITIVE.has(err.status)) {
      remove(null, PENDING_KEY);
      return true;
    }
    return false;
  } finally {
    claiming = false;
  }
}

/** For tests: forget this page load's in-flight markers. */
export function resetInviteTrackingForTests(): void {
  reporting = null;
  claiming = false;
}
